import CharacterCount from "../ui/CharacterCount";
import React, { useContext, useEffect, useRef, useState } from "react";
import Button from "../ui/Button";
import PortalDrawer from "./PortalDrawer";
import PortalContext from "../../store/PortalContext";
import { TASK_PRIORITY_OPTIONS, routeFromDealLegs } from "../../utilities/commercialTaskList";

import AuthContext from "../../store/AuthContext";
import { createCommercialTask } from "../../services/commercialTasks";

// Slide-out drawer for creating a Commercial Task from a deal. Route + Trip ID
// are derived from the deal (read-only); the user fills in the rest. The new
// task is saved to the shared commercial task list.
const PortalCreateTaskDrawer = ({ open, onClose, deal, onCreated }) => {
    const authCtx = useContext(AuthContext);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const submitting = useRef(false);
    const portalCtx = useContext(PortalContext);
    const [task, setTask] = useState("");
    const [priority, setPriority] = useState("Medium");

    // Reset the form when the drawer opens — not on close, so the fields don't
    // clear mid-animation and spoil the slide-out.
    useEffect(() => {
        if (open) {
            setError("");
            setTask("");
            setPriority("Medium");
        }
    }, [open]);

    if (!deal) return null;

    const handleClose = () => {
        if (!submitting.current) onClose();
    };

    const handleSubmit = async () => {
        if (!task.trim() || submitting.current) return;
        submitting.current = true;
        setSaving(true);
        setError("");
        try {
            const result = await createCommercialTask(
                { dealId: deal.id, salesBrokerId: portalCtx?.id, task, priority },
                authCtx?.token
            );
            if (onCreated) onCreated(result);
            onClose();
        } catch {
            setError("Could not create the task. Please try again.");
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    return (
        <PortalDrawer
            open={open}
            onClose={handleClose}
            title="Create Task"
            route={routeFromDealLegs(deal)}
            tripId={deal.avinodeTripId}
            error={error}
            actions={
                <>
                    <Button classNames={["button button--secondary"]} onClick={handleClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        classNames={["button button--primary"]}
                        onClick={handleSubmit}
                        disabled={saving || !task.trim() || !portalCtx?.id || !deal.id}
                    >
                        {saving ? "Creating…" : "Create Task"}
                    </Button>
                </>
            }
        >
            <label className="task-drawer__field">
                <span id="task-field-label">Task</span>
                <textarea
                    maxLength={2000}
                    aria-describedby="task-character-count"
                    aria-labelledby="task-field-label"
                    disabled={saving}
                    rows={3}
                    value={task}
                    onChange={(e) => setTask(e.target.value)}
                    placeholder="What needs doing?"
                />
                <CharacterCount id="task-character-count" count={task.length} maxLength={2000} />
            </label>

            <label className="task-drawer__field">
                <span>Priority</span>
                <select disabled={saving} value={priority} onChange={(e) => setPriority(e.target.value)}>
                    {TASK_PRIORITY_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                            {opt}
                        </option>
                    ))}
                </select>
            </label>
        </PortalDrawer>
    );
};

export default PortalCreateTaskDrawer;
